import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { InfoTooltip } from "@/components/InfoTooltip";
import { ChevronDown, ChevronUp, Info, Plus, Trash2 } from "lucide-react";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";

export interface LumpSumScenario {
  id: string;
  amount: number;
  source: 'savings' | 'bonus' | 'inheritance' | 'tax_refund' | 'sale_asset' | 'sale_vehicle' | 'sale_property' | 'other';
  knowsDate: boolean | null;
  date: string;
  estimatedMonthOffset: number;
  otherDescription: string;
}

export type RecurringFrequency = 'annual' | 'semiannual' | 'quarterly' | 'monthly' | 'custom';

export type RecurringSource =
  | 'annual_tax_refund'
  | 'annual_bonus'
  | 'quarterly_bonus'
  | 'business_profit'
  | 'rental_surplus'
  | 'investment_income'
  | 'sale_asset'
  | 'savings'
  | 'other';

export interface RecurringLumpSumConfig {
  enabled: boolean;
  amount: number;
  source: RecurringSource;
  otherDescription: string;
  startMode: 'date' | 'offset';
  startDate: string;
  startMonthOffset: number;
  frequency: RecurringFrequency;
  /** Months between events when frequency === 'custom'. */
  customMonths: number;
  endMode: 'occurrences' | 'date';
  occurrences: number;
  endDate: string;
}

export function emptyRecurringLumpSum(): RecurringLumpSumConfig {
  return {
    enabled: false,
    amount: 0,
    source: 'annual_tax_refund',
    otherDescription: '',
    startMode: 'offset',
    startDate: '',
    startMonthOffset: 12,
    frequency: 'annual',
    customMonths: 6,
    endMode: 'occurrences',
    occurrences: 5,
    endDate: '',
  };
}

export interface LumpSumConfig {
  scenarios: LumpSumScenario[];
  continueAfterPayoff: boolean | null;
  recurring?: RecurringLumpSumConfig;
}

/** Balance at each payment period for the policy loan (period 1 = first payment) */
export interface PolicyLoanBalanceByPeriod {
  period: number;
  balance: number;
}

interface LumpSumScenariosProps {
  config: LumpSumConfig;
  onChange: (config: LumpSumConfig) => void;
  jurisdiction: Jurisdiction;
  loanStartDate: string;
  loanEndDate: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Payment-period balances from the base (no lump sum) policy loan */
  policyBalanceByPeriod?: PolicyLoanBalanceByPeriod[];
  /** The payment number at which the policy loan is fully repaid (base scenario) */
  policyPayoffPayment?: number;
}

const sourceLabels: Record<string, string> = {
  savings: 'Personal Savings',
  bonus: 'Bonus / Commission',
  inheritance: 'Inheritance / Gift',
  tax_refund: 'Tax Refund',
  sale_asset: 'Sale of Asset',
  sale_vehicle: 'Sale of Vehicle',
  sale_property: 'Sale of Property',
  other: 'Other',
};

const recurringSourceLabels: Record<RecurringSource, string> = {
  annual_tax_refund: 'Annual Tax Refund',
  annual_bonus: 'Annual Bonus',
  quarterly_bonus: 'Quarterly Bonus',
  business_profit: 'Business Profit Distribution',
  rental_surplus: 'Rental Property Surplus',
  investment_income: 'Investment Income',
  sale_asset: 'Sale of Asset',
  savings: 'Personal Savings',
  other: 'Other',
};

const recurringFrequencyLabels: Record<RecurringFrequency, string> = {
  annual: 'Annual (every 12 months)',
  semiannual: 'Semi-annual (every 6 months)',
  quarterly: 'Quarterly (every 3 months)',
  monthly: 'Monthly',
  custom: 'Custom interval (months)',
};

export function LumpSumScenarios({ config, onChange, jurisdiction, loanStartDate, loanEndDate, isOpen, onOpenChange, policyBalanceByPeriod, policyPayoffPayment }: LumpSumScenariosProps) {
  const { fmt, currencySymbol } = createFormatter(jurisdiction);

  const update = (scenarios: LumpSumScenario[]) => onChange({ ...config, scenarios });

  const addScenario = () => {
    if (config.scenarios.length >= 2) return;
    const newScenario: LumpSumScenario = {
      id: `lump-${Date.now()}`,
      amount: 0,
      source: 'savings',
      knowsDate: null,
      date: '',
      estimatedMonthOffset: 12,
      otherDescription: '',
    };
    update([...config.scenarios, newScenario]);
  };

  const removeScenario = (id: string) => {
    update(config.scenarios.filter(s => s.id !== id));
  };

  const updateScenario = (id: string, partial: Partial<LumpSumScenario>) => {
    update(config.scenarios.map(s => s.id === id ? { ...s, ...partial } : s));
  };

  /** Get approximate month offset from a specific date */
  const getMonthOffsetFromDate = (date: string): number => {
    if (!date || !loanStartDate) return 0;
    const start = new Date(loanStartDate);
    const target = new Date(date);
    const months = (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth());
    return Math.max(1, Math.round(months));
  };

  /** Get balance info for a given month offset */
  const getBalanceAtMonth = (monthOffset: number): { balance: number; isPastPayoff: boolean } | null => {
    if (!policyBalanceByPeriod || policyBalanceByPeriod.length === 0) return null;
    // monthOffset corresponds roughly to payment period
    const period = monthOffset;
    if (period < 1) return null;

    const payoff = policyPayoffPayment ?? policyBalanceByPeriod.length;
    if (period > payoff) {
      return { balance: 0, isPastPayoff: true };
    }

    const row = policyBalanceByPeriod.find(r => r.period === period);
    if (row) return { balance: row.balance, isPastPayoff: false };

    // If exact period not found, use closest prior
    const prior = policyBalanceByPeriod.filter(r => r.period <= period);
    if (prior.length > 0) return { balance: prior[prior.length - 1].balance, isPastPayoff: false };

    return null;
  };

  const validateDate = (date: string): string | null => {
    if (!date) return null;
    if (loanStartDate && date < loanStartDate) {
      return 'Lump sum date must be on or after the loan start date.';
    }
    if (loanEndDate && date > loanEndDate) {
      return 'Lump sum date must be within the planned amortization period.';
    }
    return null;
  };

  const hasActiveLumpSums = config.scenarios.some(s => s.amount > 0);
  const recurring: RecurringLumpSumConfig = config.recurring ?? emptyRecurringLumpSum();
  const updateRecurring = (partial: Partial<RecurringLumpSumConfig>) => {
    onChange({ ...config, recurring: { ...recurring, ...partial } });
  };
  const hasActiveRecurring = recurring.enabled && recurring.amount > 0;
  const hasEarlyPayoffPotential = hasActiveLumpSums || hasActiveRecurring;

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="border-border/60">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors rounded-t-lg"
          >
            <div className="text-left">
              <p className="text-sm font-semibold">Advanced: Lump Sum Payment Scenarios</p>
              <p className="text-[10px] text-muted-foreground">Model one-time extra principal payments to see accelerated payoff impact.</p>
            </div>
            {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {config.scenarios.map((scenario, idx) => {
              const dateError = scenario.knowsDate && scenario.date ? validateDate(scenario.date) : null;

              return (
                <div key={scenario.id} className="p-3 rounded-lg bg-muted/30 border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Lump Sum #{idx + 1}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeScenario(scenario.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Lump Sum Amount ({currencySymbol})</Label>
                      <CurrencyInput
                        value={scenario.amount === 0 ? null : scenario.amount}
                        onChange={(v) => updateScenario(scenario.id, { amount: v ?? 0 })}
                        min={0}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Source</Label>
                      <Select value={scenario.source} onValueChange={(v: LumpSumScenario['source']) => updateScenario(scenario.id, { source: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(sourceLabels).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Other description - mandatory when source is 'other' */}
                  {scenario.source === 'other' && (
                    <div>
                      <Label className="text-xs">Description (required)</Label>
                      <Textarea
                        value={scenario.otherDescription || ''}
                        onChange={e => updateScenario(scenario.id, { otherDescription: e.target.value })}
                        placeholder="Please describe the source of this lump sum..."
                        className="min-h-[60px] text-xs"
                      />
                      {!(scenario.otherDescription || '').trim() && (
                        <p className="text-[10px] text-destructive mt-1">A description is required when "Other" is selected.</p>
                      )}
                    </div>
                  )}

                  {/* Timing question */}
                  <div className="space-y-2">
                    <Label className="text-xs">Do you know when this is expected?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => updateScenario(scenario.id, { knowsDate: true })}
                        className={`rounded-md border p-2 text-xs text-left transition-colors ${
                          scenario.knowsDate === true
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/50'
                        }`}
                      >
                        Yes — I know the date
                      </button>
                      <button
                        type="button"
                        onClick={() => updateScenario(scenario.id, { knowsDate: false })}
                        className={`rounded-md border p-2 text-xs text-left transition-colors ${
                          scenario.knowsDate === false
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/50'
                        }`}
                      >
                        No — estimate timing
                      </button>
                    </div>
                  </div>

                  {scenario.knowsDate === true && (() => {
                    const monthOff = scenario.date ? getMonthOffsetFromDate(scenario.date) : 0;
                    const balInfo = monthOff > 0 ? getBalanceAtMonth(monthOff) : null;

                    return (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Lump Sum Date</Label>
                        <Input
                          type="date"
                          value={scenario.date}
                          onChange={e => updateScenario(scenario.id, { date: e.target.value })}
                          min={loanStartDate}
                          max={loanEndDate || undefined}
                        />
                        {dateError && (
                          <p className="text-[10px] text-destructive mt-1">{dateError}</p>
                        )}
                        {balInfo?.isPastPayoff && (
                          <div className="flex items-start gap-1.5 p-2 rounded-md bg-destructive/10 border border-destructive/30">
                            <Info className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                            <p className="text-[10px] text-destructive leading-relaxed">
                              The policy loan is already fully repaid by payment {policyPayoffPayment}. A lump sum cannot be applied at this date. Please select an earlier date.
                            </p>
                          </div>
                        )}
                        {balInfo && !balInfo.isPastPayoff && balInfo.balance > 0 && (
                          <div className="flex items-start gap-1.5 p-2 rounded-md bg-accent/10 border border-accent/30">
                            <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                            <p className="text-[10px] text-muted-foreground leading-relaxed">
                              Expected remaining policy loan balance at this date: <span className="font-semibold text-foreground">{fmt(balInfo.balance)}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {scenario.knowsDate === false && (() => {
                    const balInfo = scenario.estimatedMonthOffset > 0 ? getBalanceAtMonth(scenario.estimatedMonthOffset) : null;

                    return (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Estimated month from loan start</Label>
                        <Input
                          type="number"
                          min={1}
                          max={360}
                          value={scenario.estimatedMonthOffset || ''}
                          onChange={e => updateScenario(scenario.id, { estimatedMonthOffset: Number(e.target.value) })}
                          placeholder="e.g. 12 for 1 year"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Approximate number of months after the first payment date.
                        </p>
                        {balInfo?.isPastPayoff && (
                          <div className="flex items-start gap-1.5 p-2 rounded-md bg-destructive/10 border border-destructive/30">
                            <Info className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                            <p className="text-[10px] text-destructive leading-relaxed">
                              The policy loan is already fully repaid by payment {policyPayoffPayment}. A lump sum cannot be applied at month {scenario.estimatedMonthOffset}. Please select an earlier month.
                            </p>
                          </div>
                        )}
                        {balInfo && !balInfo.isPastPayoff && balInfo.balance > 0 && (
                          <div className="flex items-start gap-1.5 p-2 rounded-md bg-accent/10 border border-accent/30">
                            <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                            <p className="text-[10px] text-muted-foreground leading-relaxed">
                              Expected remaining policy loan balance at month {scenario.estimatedMonthOffset}: <span className="font-semibold text-foreground">{fmt(balInfo.balance)}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}

            {config.scenarios.length < 2 && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={addScenario}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Lump Sum Scenario {config.scenarios.length === 0 ? '' : '#2'}
              </Button>
            )}

            {/* Recurring Lump Sum Strategy */}
            <div className="p-3 rounded-lg border border-border bg-card space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">Recurring Lump Sum Strategy</p>
                  <p className="text-[10px] text-muted-foreground">
                    Model a repeating extra principal payment — e.g. an annual bonus or
                    quarterly profit distribution — directed at this loan.
                  </p>
                </div>
                <Switch
                  checked={recurring.enabled}
                  onCheckedChange={(c) => updateRecurring({ enabled: c })}
                  aria-label="Enable recurring lump sum"
                />
              </div>

              {recurring.enabled && (
                <div className="space-y-3 pt-2 border-t border-border">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Recurring Amount ({currencySymbol})</Label>
                      <CurrencyInput
                        value={recurring.amount === 0 ? null : recurring.amount}
                        onChange={(v) => updateRecurring({ amount: v ?? 0 })}
                        min={0}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Source</Label>
                      <Select
                        value={recurring.source}
                        onValueChange={(v: RecurringSource) => updateRecurring({ source: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(Object.keys(recurringSourceLabels) as RecurringSource[]).map((k) => (
                            <SelectItem key={k} value={k}>{recurringSourceLabels[k]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {recurring.source === 'other' && (
                    <div>
                      <Label className="text-xs">Describe the source (required)</Label>
                      <Textarea
                        value={recurring.otherDescription}
                        onChange={(e) => updateRecurring({ otherDescription: e.target.value })}
                        placeholder="Describe the recurring source..."
                        className="min-h-[50px] text-xs"
                      />
                      {!recurring.otherDescription.trim() && (
                        <p className="text-[10px] text-destructive mt-1">A description is required when "Other" is selected.</p>
                      )}
                    </div>
                  )}

                  {/* Frequency */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Frequency</Label>
                      <Select
                        value={recurring.frequency}
                        onValueChange={(v: RecurringFrequency) => updateRecurring({ frequency: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(Object.keys(recurringFrequencyLabels) as RecurringFrequency[]).map((k) => (
                            <SelectItem key={k} value={k}>{recurringFrequencyLabels[k]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {recurring.frequency === 'custom' && (
                      <div>
                        <Label className="text-xs">Months between events</Label>
                        <Input
                          type="number"
                          min={1}
                          max={120}
                          value={recurring.customMonths || ''}
                          onChange={(e) => updateRecurring({ customMonths: Number(e.target.value) })}
                          placeholder="e.g. 6"
                        />
                      </div>
                    )}
                  </div>

                  {/* Start */}
                  <div className="space-y-2">
                    <Label className="text-xs">First event</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => updateRecurring({ startMode: 'offset' })}
                        className={`rounded-md border p-2 text-xs text-left transition-colors ${
                          recurring.startMode === 'offset'
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/50'
                        }`}
                      >
                        Estimate by month offset
                      </button>
                      <button
                        type="button"
                        onClick={() => updateRecurring({ startMode: 'date' })}
                        className={`rounded-md border p-2 text-xs text-left transition-colors ${
                          recurring.startMode === 'date'
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/50'
                        }`}
                      >
                        Pick a specific date
                      </button>
                    </div>
                    {recurring.startMode === 'offset' ? (
                      <Input
                        type="number"
                        min={0}
                        max={360}
                        value={recurring.startMonthOffset || ''}
                        onChange={(e) => updateRecurring({ startMonthOffset: Number(e.target.value) })}
                        placeholder="Months after loan start (e.g. 12)"
                      />
                    ) : (
                      <Input
                        type="date"
                        value={recurring.startDate}
                        onChange={(e) => updateRecurring({ startDate: e.target.value })}
                        min={loanStartDate}
                      />
                    )}
                  </div>

                  {/* End */}
                  <div className="space-y-2">
                    <Label className="text-xs">End condition</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => updateRecurring({ endMode: 'occurrences' })}
                        className={`rounded-md border p-2 text-xs text-left transition-colors ${
                          recurring.endMode === 'occurrences'
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/50'
                        }`}
                      >
                        Number of occurrences
                      </button>
                      <button
                        type="button"
                        onClick={() => updateRecurring({ endMode: 'date' })}
                        className={`rounded-md border p-2 text-xs text-left transition-colors ${
                          recurring.endMode === 'date'
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/50'
                        }`}
                      >
                        End date
                      </button>
                    </div>
                    {recurring.endMode === 'occurrences' ? (
                      <Input
                        type="number"
                        min={1}
                        max={600}
                        value={recurring.occurrences || ''}
                        onChange={(e) => updateRecurring({ occurrences: Number(e.target.value) })}
                        placeholder="e.g. 5"
                      />
                    ) : (
                      <Input
                        type="date"
                        value={recurring.endDate}
                        onChange={(e) => updateRecurring({ endDate: e.target.value })}
                        min={loanStartDate}
                      />
                    )}
                  </div>

                  <div className="flex items-start gap-1.5 p-2 rounded-md bg-muted/40 border border-border">
                    <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                      Recurring lump sums reduce policy and intended loan principal on each event date.
                      Updated payoff timing and Family Financing Margin™ appear in the summary above.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Early Payoff Continuation Option */}
            {hasEarlyPayoffPotential && (
              <div className="p-3 rounded-lg bg-accent/5 border border-accent/20 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">
                    After the loan is repaid early, do you want to continue the original committed payment schedule and redirect those payments into your family financing strategy as system funding?
                  </Label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => onChange({ ...config, continueAfterPayoff: true })}
                    className={`rounded-md border p-2.5 text-xs text-left transition-colors ${
                      config.continueAfterPayoff === true
                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <p className="font-semibold">Yes</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Continue original payment schedule</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange({ ...config, continueAfterPayoff: false })}
                    className={`rounded-md border p-2.5 text-xs text-left transition-colors ${
                      config.continueAfterPayoff === false
                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <p className="font-semibold">No</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Stop payments when loan is repaid</p>
                  </button>
                </div>

                {/* Contextual message for YES */}
                {config.continueAfterPayoff === true && (
                  <div className="p-3 rounded-md border border-emerald-300/40 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-700/30 space-y-1.5">
                    <p className="text-xs text-emerald-900 dark:text-emerald-200 leading-relaxed">
                      That sounds great. Please see and refer to the updated payments recommended to redirect into your system. You may want to connect and reach out to your advisor/coach to discuss how best to implement this.
                    </p>
                    <p className="text-[10px] text-emerald-800/80 dark:text-emerald-300/70 leading-relaxed">
                      Commitment is part of both the opportunity and the systematic behavior you can demonstrate to other members of the family system. The ripple effect of this ongoing behavior can cascade across generations.
                    </p>
                  </div>
                )}

                {/* Contextual message for NO */}
                {config.continueAfterPayoff === false && (
                  <div className="p-3 rounded-md border border-red-300/40 bg-red-50/60 dark:bg-red-950/20 dark:border-red-700/30 space-y-1.5">
                    <p className="text-xs text-red-900 dark:text-red-200 leading-relaxed font-medium">
                      Are you sure?
                    </p>
                    <p className="text-[10px] text-red-800/80 dark:text-red-300/70 leading-relaxed">
                      Choosing no is certainly fine and your choice as the person playing the role of "the banker." However, if you choose yes it reflects the principle of continuing disciplined repayment behavior even after the debt is gone, so those dollars can now strengthen your family financing strategy. Nelson Nash would often say, "you have to finish your loan schedule." Commitment is part of both the opportunity and the systematic behavior you can demonstrate to other members of the family system. The ripple effect of this ongoing behavior can cascade across generations.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
